import intersection from 'lodash/intersection';
import { EntitlementsUser } from '@adsk/alloy-provider';
import { ALL_USERS_SPECIAL_ID } from '../constants/roles-types';
import { sortAlphabeticallyByPath } from './sortingHelpers';
import {
  Assignee,
  AssigneeType,
  EntityStatus,
  OxygenCompany,
  OxygenEntity,
  OxygenId,
  OxygenRole,
  OxygenUser,
} from '../sdk';
import { Oxygen } from '../providers/OxygenProvider';
import { translate } from './i18n';
import { EMPTY_ARRAY, HQUserStatus } from '../constants';

export const getAssigneeFromOxygenEntity = ({
  id,
  type,
}: Pick<OxygenEntity, 'id' | 'type'>): Assignee => ({ id, type });

/**
 * Get users list based on users, roles & companies.
 * This method returns a list of all users that either exists in assigneesArray or their role or company exits there.
 * @param assigneesArray - array of users, roles & companies.
 * @param oxygen
 */
export const getUsersList = (
  assigneesArray: Assignee[],
  oxygen: Pick<Oxygen, 'users'>,
): OxygenUser[] => {
  const userIds = assigneesArray
    .filter((obj) => obj.type === AssigneeType.User)
    .map((obj) => obj.id);
  const roleIds = assigneesArray
    .filter((obj) => obj.type === AssigneeType.Role)
    .map((obj) => obj.id);
  const companyIds = assigneesArray
    .filter((obj) => obj.type === AssigneeType.Company)
    .map((obj) => obj.id);

  return sortAlphabeticallyByPath(
    Object.keys(oxygen.users)
      .filter((userId) => {
        const user = oxygen.users[userId];
        return (
          userIds.includes(ALL_USERS_SPECIAL_ID) ||
          userIds.includes(userId) ||
          (user.company && companyIds.includes(user.company.oxygenId)) ||
          intersection(
            roleIds,
            user.roles.map((role: Record<string, string>) => role.oxygenId),
          ).length
        );
      })
      .map((userId) => oxygen.users[userId]),
    'name',
  );
};

/** Example of sorted names order with current user "Jack Daniels":
 * Jack Daniels
 * Avi
 * Former member
 * simha-rotman@gmail.com
 * Tomer
 * Zach
 */
export const sortAssigneesWithCurrentUserFirst = (
  assignees: Assignee[],
  oxygen: Oxygen,
  currentUser: Pick<EntitlementsUser, 'autodeskId'> | undefined,
) => {
  if (oxygen.loading || !assignees.length || !currentUser) {
    return assignees;
  }

  const currentUserId = currentUser?.autodeskId;
  const assigneesWithoutCurrentUser = assignees.filter(({ id }) => id !== currentUserId);
  const sortedAssignees = sortAssigneesIncludingFormerAssigneesByName(
    oxygen,
    assigneesWithoutCurrentUser,
  );

  const isCurrentUserInAssignees = assigneesWithoutCurrentUser.length < assignees.length;
  return isCurrentUserInAssignees
    ? [{ id: currentUserId as string, type: AssigneeType.User }, ...sortedAssignees]
    : sortedAssignees;
};

export const getFormerUser = (id: OxygenId): OxygenUser =>
  ({
    id,
    autodeskId: id,
    name: translate('formerUser'),
    oxygenType: AssigneeType.User,
    type: AssigneeType.User,
    imageUrl: '',
    company: { id: '', name: '', oxygenId: '' },
    status: EntityStatus.Deleted,
  } as OxygenUser);

export const getFormerRole = (id: OxygenId): OxygenRole =>
  ({
    id,
    name: translate('formerRole'),
    oxygenType: AssigneeType.Role,
    type: AssigneeType.Role,
    status: EntityStatus.Inactive,
  } as OxygenRole);

export const getFormerCompany = (id: OxygenId): OxygenCompany =>
  ({
    id,
    name: translate('formerCompany'),
    oxygenType: AssigneeType.Company,
    type: AssigneeType.Company,
    status: EntityStatus.Deleted,
  } as OxygenCompany);

export const mapAssigneesToOxygenObjects = (
  oxygen: Oxygen,
  assignees?: Assignee[] | null,
): OxygenEntity[] =>
  assignees?.map(({ id, type }) => {
    switch (type) {
      case AssigneeType.User:
        return oxygen.users[id] ?? getFormerUser(id);
      case AssigneeType.Role:
        return oxygen.roles[id] ?? getFormerRole(id);
      case AssigneeType.Company:
        return oxygen.companies[id] ?? getFormerCompany(id);
      default:
        return getFormerUser(id);
    }
  }) || EMPTY_ARRAY;

export const getAssigneeDisplayName = (oxygen: Oxygen, assignee: Assignee): string => {
  if (oxygen.loading) {
    return '';
  }

  const [oxygenEntity] = mapAssigneesToOxygenObjects(oxygen, [assignee]);
  const isDeletedUser =
    assignee.type === AssigneeType.User && oxygenEntity.status === HQUserStatus.DELETED;
  if (isDeletedUser) {
    return translate('REMOVED_USER_FORMAT', {
      username: oxygenEntity.name,
    });
  }

  return oxygenEntity.name;
};

export const getAssigneeCompanyName = (oxygen: Oxygen, assignee: Assignee): string => {
  if (assignee.type !== AssigneeType.User || oxygen.loading) {
    return '';
  }

  const [userEntity] = mapAssigneesToOxygenObjects(oxygen, [assignee]) as OxygenUser[];
  return userEntity.status === HQUserStatus.DELETED ? '' : userEntity.company.name;
};

export const getAssigneeDisplayNameWithCompanyName = (
  oxygen: Oxygen,
  assignee: Assignee,
): string => {
  if (oxygen.loading) {
    return '';
  }

  const displayName = getAssigneeDisplayName(oxygen, assignee);
  const companyName = getAssigneeCompanyName(oxygen, assignee);
  return companyName ? `${displayName} (${companyName})` : displayName;
};

export const getAssigneeImageUrl = (oxygen: Oxygen, assignee: Assignee): string => {
  if (oxygen.loading) {
    return '';
  }

  const [oxygenEntity] = mapAssigneesToOxygenObjects(oxygen, [assignee]);
  if (oxygenEntity.type === AssigneeType.Role) {
    return '';
  }

  return oxygenEntity.imageUrl || '';
};

/** Example of sorted names order:
 * Avi
 * Former member
 * simha-rotman@gmail.com
 * Tomer
 * Zach
 */
export const sortAssigneesIncludingFormerAssigneesByName = (
  oxygen: Oxygen,
  assignees?: Assignee[] | null,
): Assignee[] => {
  if (oxygen.loading || !assignees?.length) {
    return assignees || EMPTY_ARRAY;
  }

  const oxygenEntities = mapAssigneesToOxygenObjects(oxygen, assignees || []);
  const sortedOxygenEntities = sortAlphabeticallyByPath(oxygenEntities, 'name');
  return sortedOxygenEntities.map(getAssigneeFromOxygenEntity);
};
